import React, { useState, useEffect } from 'react';
import { API } from '../utils/api';

import LucideIcon from './LucideIcon';
import { getLeaderboardIcon } from '../utils/iconMapper';

interface LeaderboardUser {
  id: string;
  username: string;
  email?: string;
  points: number;
  level: number;
  rank: number;
  streak: number;
  challenges: number;
  quests?: number;
  badges: (string | { name?: string; icon?: string })[];
}

// Keep in sync with the badge lists in Profile.tsx and GamificationHub.tsx.
const BADGE_ICONS: { [key: string]: string } = {
  'New Recruit': 'Star', 'Newcomer': 'Star', 'First Mission': 'Target', 'First Success': 'Target',
  'Quick Learner': 'Zap', 'Practice Ace': 'Award', 'Elite Commander': 'Shield', 'Grandmaster': 'Crown',
  'Momentum Builder': 'Zap', 'Consistent Streak': 'Rocket', 'Unstoppable Streak': 'Shield', 'Iron Discipline': 'Star',
  'Focus Stone': 'Gem', 'Archive Keeper': 'ScrollText', 'Archive Master': 'Library', 'Grand Champion': 'Sparkles',
  'Apprentice Graduate': 'GraduationCap', 'Grand Master': 'Trophy', 'Challenge Master': 'Trophy',
  'Getting Consistent': 'Flame', 'Streak Master': 'Flame', 'Point Collector': 'Star', 'Point Hunter': 'Gem',
};

function getBadgeIconKey(badge: string | { name?: string; icon?: string }): string {
  if (typeof badge === 'object' && badge.icon) return badge.icon;
  const badgeName = typeof badge === 'string' ? badge : badge.name || '';
  return BADGE_ICONS[badgeName] || 'Star';
}

/** Podium tiers use the same escalating accents Profile's badge rarity does,
 * so "1st place" reads as special the same way "legendary" does elsewhere --
 * one system, not a leaderboard-specific palette. */
const PODIUM_STYLE = [
  { bg: 'oklch(0.8 0.15 85 / 0.16)', border: 'var(--gold)', fg: 'oklch(0.55 0.13 75)' }, // 1st -- gold
  { bg: 'var(--surface-sunken)', border: 'var(--border-strong)', fg: 'var(--text-secondary)' }, // 2nd -- neutral
  { bg: 'var(--ember-soft)', border: 'var(--ember)', fg: 'var(--ember-strong)' }, // 3rd -- ember
];

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

const Leaderboard: React.FC = () => {
  const [users, setUsers] = useState<LeaderboardUser[]>([]);

  const loadLeaderboard = async () => {
    try {
      const response = await API.get('/leaderboard');
      if (response && response.users) {
        setUsers(response.users);
      }
    } catch (error) {
      // Error loading leaderboard
    }
  };

  useEffect(() => {
    loadLeaderboard();
    const handleChallengeCompleted = () => { setTimeout(loadLeaderboard, 1500); };
    window.addEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
    return () => window.removeEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
  }, []);

  window.refreshLeaderboard = loadLeaderboard;

  // Look up by rank, not array position -- the real backend's query is
  // already rank-ordered so this rarely differs today, but assuming array
  // order matches `rank` is exactly the kind of assumption that silently
  // breaks the moment either changes independently of the other.
  const topThree = [1, 2, 3]
    .map((rank) => users.find((u) => u.rank === rank))
    .filter((u): u is LeaderboardUser => !!u);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 900 }}>
      <div className="ws-fade-up" style={panel}>
        <h1 className="ws-display" style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Leaderboard</h1>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>All-time ranking by total XP earned.</p>
      </div>

      {topThree.length === 3 && (
        <div className="ws-fade-up" style={{ ...panel, animationDelay: '60ms' }}>
          <h2 className="ws-display" style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Top performers</h2>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 24 }}>
            {[topThree[1], topThree[0], topThree[2]].map((user) => {
              const place = user.rank;
              const style = PODIUM_STYLE[place - 1];
              const ringSize = place === 1 ? 76 : 60;
              return (
                <div key={user.id} style={{ textAlign: 'center' }}>
                  <div style={{
                    width: ringSize, height: ringSize, borderRadius: '50%', background: style.bg,
                    border: `2px solid ${style.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 10, color: style.fg,
                  }}>
                    <LucideIcon name={getLeaderboardIcon(place)} size={place === 1 ? 34 : 26} />
                  </div>
                  <p className="ws-display" style={{ margin: '0 0 2px', fontSize: place === 1 ? 15 : 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{user.username}</p>
                  <p className="ws-mono" style={{ margin: 0, fontSize: 12, color: style.fg, fontWeight: 700 }}>{user.points} pts</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="ws-fade-up" style={{ ...panel, animationDelay: '100ms' }}>
        <h2 className="ws-display" style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Full rankings</h2>

        {users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, color: 'var(--text-tertiary)' }}><LucideIcon name="Trophy" size={40} /></div>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>Nobody&rsquo;s on the board yet — finish a lesson to claim the first spot.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {users.map((u) => {
              const isCurrentUser = u.id === 'current-user';
              return (
                <div
                  key={u.id}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap',
                    padding: '12px 16px', borderRadius: 10,
                    background: isCurrentUser ? 'var(--ember-soft)' : 'var(--surface-sunken)',
                    border: `1.5px solid ${isCurrentUser ? 'var(--ember)' : 'var(--border)'}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                    <span className="ws-mono" style={{ width: 28, textAlign: 'center', flex: '0 0 auto', color: u.rank <= 3 ? 'var(--ember-strong)' : 'var(--text-tertiary)' }}>
                      {u.rank <= 3 ? <LucideIcon name={getLeaderboardIcon(u.rank)} size={19} /> : `#${u.rank}`}
                    </span>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--ember)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                      <span className="ws-display" style={{ fontSize: 13, fontWeight: 700 }}>{u.username[0]?.toUpperCase()}</span>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{u.username}</span>
                        {u.badges.slice(0, 3).map((badge, idx) => (
                          <span key={idx} title={typeof badge === 'string' ? badge : badge.name} style={{ color: 'var(--ember-strong)', display: 'flex' }}>
                            <LucideIcon name={getBadgeIconKey(badge)} size={13} />
                          </span>
                        ))}
                      </div>
                      <span className="ws-mono" style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Level {u.level} · {u.badges.length} badges</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 20 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div className="ws-display" style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-primary)' }}>{u.points}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>Total XP</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div className="ws-display" style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-primary)' }}>{u.challenges}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>Lessons</div>
                    </div>
                    {u.quests !== undefined && (
                      <div style={{ textAlign: 'center' }}>
                        <div className="ws-display" style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-primary)' }}>{u.quests}/5</div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>Quests</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
